import orderBy from 'lodash/orderBy';

export const compareDistance = (a, b) => {
  const distanceA = a.distance;
  const distanceB = b.distance;

  let comparison = 0;
  if (distanceA > distanceB) {
    comparison = 1;
  } else if (distanceA < distanceB) {
    comparison = -1;
  }
  return comparison;
};

export const addOrUpdateIdempotently = (newItem, items) => {
  const matchId = items.findIndex(item => item.id === newItem.id);

  if (matchId !== -1) {
    items.splice(matchId, 1, newItem);
    return items;
  } else {
    return [newItem, ...items];
  }
};

export const sortDatedItems = unsortedDatedItems => {
  let sortedDatedItems = unsortedDatedItems.sort((datedItemA, datedItemB) => {
    if (datedItemA.endYear > datedItemB.endYear) {
      return -1;
    }

    if (datedItemA.endYear < datedItemB.endYear) {
      return 1;
    }

    if (datedItemA.endYear === datedItemB.endYear) {
      if (datedItemA.endMonth > datedItemB.endMonth) {
        return -1;
      }

      if (datedItemA.endMonth < datedItemB.endMonth) {
        return 1;
      }
    }

    return 0;
  });

  return sortedDatedItems;
};

export const sortObjectsByDate = (objects, options = {}) => {
  const dateKey = options['dateKey'] || 'date';
  const order = options['order'] || 'desc';

  return orderBy(objects, dateKey, order);
};

export const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

// The Resume Builder API requires startMonth/startYear/endMonth/endYear to be
// numbers, but profile data sourced from the server can contain null for entries
// where dates were left blank (e.g. a current role). Coerce null → 0 so the API
// never receives null for these numeric fields.
export const sanitizeCompanyDates = <
  T extends {
    startMonth?: number | null;
    startYear?: number | null;
    endMonth?: number | null;
    endYear?: number | null;
  },
>(
  company: T
): T => ({
  ...company,
  startMonth: company.startMonth ?? 0,
  startYear: company.startYear ?? 0,
  endMonth: company.endMonth ?? 0,
  endYear: company.endYear ?? 0,
});

// `dates` is a display string the server derives from the numeric month/year
// fields (UserCompany.getDates / UserSchool.getDates) and refuses on write --
// "id and dates should not be used for creation". The work-history and education
// forms keep an optimistic copy so the list updates before the save round-trips,
// which means these have to reproduce the server's output. If they don't, the
// label the user sees on edit differs from the one that arrives on the next
// fetch, and the format appears to change by itself.
//
// Server format, for reference:
//   company: `Jan, 2020 - Feb, 2021` / `Jan, 2020 - Present`
//   school:  `2020 - 2021`           / `2020 - Present`
const MONTH_ABBR = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

// The server guards this with `startMonth != 9999`, which misses 0 -- the value
// sanitizeCompanyDates writes for a blank month -- and then concatenates the
// null that translateMonth returns, so saved entries with no month render as
// "null, 2020". Omitting the month is what that guard was reaching for; we do
// not reproduce the "null, " prefix.
const monthPrefix = (month: number) =>
  month >= 1 && month <= 12 ? `${MONTH_ABBR[month - 1]}, ` : '';

// endYear 9999 is the "current position" sentinel; 0 means it was never filled
// in. The server treats both as ongoing.
const isOngoing = (endYear: number) => endYear === 9999 || endYear === 0;

export const formatCompanyDates = ({
  startMonth,
  startYear,
  endMonth,
  endYear,
}: {
  startMonth: number;
  startYear: number;
  endMonth: number;
  endYear: number;
}): string =>
  `${monthPrefix(startMonth)}${startYear} - ${
    isOngoing(endYear) ? 'Present' : `${monthPrefix(endMonth)}${endYear}`
  }`;

export const formatSchoolDates = ({
  startYear,
  endYear,
}: {
  startYear: number;
  endYear: number;
}): string => `${startYear} - ${endYear === 9999 ? 'Present' : endYear}`;
